import type { TFunction } from 'shared/translations';
import Button from '@precis-digital/kurama/Button';
import Chip from '@precis-digital/kurama/Chip';
import Grid from '@precis-digital/kurama/Grid';
import AdvancedFilterIcon from '@precis-digital/kurama/icons/AdvancedFilterIcon';
import { styled } from '@precis-digital/kurama/utils';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import type { FilterTranslationNamespace } from './types';
interface AddFiltersGridProps<T> {
  activeFilters: T[];
  handleOnFilterClick: ({
    showActiveFilters
  }: {
    showActiveFilters?: boolean;
  }) => void;
  parseFilterToChipLabel: (filter: T) => string;
  onUpdateFilters: (filters: T[]) => void;
  t: TFunction<FilterTranslationNamespace>;
  containerRef?: RefObject<HTMLDivElement | null>;
  /**
   * When true, the chip is shown in a disabled style and cannot be opened for edit.
   * Delete remains available so users can still clear the filter.
   */
  isFilterDisabled?: (filter: T) => boolean;
  /**
   * Tooltip shown when hovering a disabled filter chip.
   */
  disabledFilterTooltip?: string;
}
const AddFiltersGrid = <T,>({
  activeFilters,
  handleOnFilterClick,
  parseFilterToChipLabel,
  onUpdateFilters,
  t,
  containerRef,
  isFilterDisabled,
  disabledFilterTooltip
}: AddFiltersGridProps<T>): ReactElement => {
  const ref = useRef<HTMLDivElement>(null);
  const filtersGrid = ref.current;
  const [filterGridWidth, setFilterGridWidth] = useState(20);
  const [numberOfHiddenFilters, setNumberOfHiddenFilters] = useState(0);
  useEffect(() => {
    if (filtersGrid != null) {
      const resizeObserver = new ResizeObserver(() => {
        setFilterGridWidth(filtersGrid.clientWidth);
      });
      resizeObserver.observe(filtersGrid);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [filtersGrid]);
  useEffect(() => {
    if (filtersGrid == null) {
      return;
    }
    const options = {
      root: filtersGrid,
      threshold: 1
    };
    const observer = new IntersectionObserver(entries => {
      setNumberOfHiddenFilters(entries.filter(entry => {
        return !entry.isIntersecting;
      }).length);
    }, options);
    for (const [index] of activeFilters.entries()) {
      const childNode = filtersGrid.children?.[index];
      if (childNode != null) {
        observer.observe(childNode);
      }
    }
    return () => {
      observer.disconnect();
    };
  }, [filtersGrid, activeFilters, filterGridWidth]);
  return <StyledAddFiltersGrid ref={containerRef} hasFilters={activeFilters.length > 0} display="flex" gap="8px" alignItems="center" overflow="hidden">
      {activeFilters.length > 0 && <Grid ref={ref} item xs display="flex" position="relative" gap="8px" overflow="hidden">
          {activeFilters.map((filter, index_0) => {
        const disabled = isFilterDisabled?.(filter) === true;
        const disabledTooltip = disabled ? disabledFilterTooltip : undefined;
        const chip = <Chip label={parseFilterToChipLabel(filter)} isDisabled={disabled} onDelete={() => {
          const currentFilters = activeFilters.filter((_, i) => i !== index_0);
          onUpdateFilters(currentFilters);
        }} {...!disabled && {
          onClick: () => {
            handleOnFilterClick({
              showActiveFilters: true
            });
          }
        }} />;
        return <Grid key={`${(filter as {
          parameter?: {
            value?: string;
          };
        })?.parameter?.value}-${index_0}`}>
                {disabledTooltip != null && disabledTooltip !== '' ? <Tooltip kind="singleline" title={disabledTooltip}>
                    <span>{chip}</span>
                  </Tooltip> : chip}
              </Grid>;
      })}
          {numberOfHiddenFilters > 0 && <StyledGradientGrid position="absolute" top={0} width="32px" height="32px" />}
        </Grid>}

      {activeFilters.length > 0 && numberOfHiddenFilters > 0 && <Tooltip kind="multiline" title="Click to view active filters" body={<Grid>
              {activeFilters.map((filter_0, index_1) => <li key={`${(filter_0 as {
        parameter?: {
          value?: string;
        };
      })?.parameter?.value}-${index_1}`}>
                  {parseFilterToChipLabel(filter_0)}
                </li>)}
            </Grid>}>
          <Button onClick={() => {
        handleOnFilterClick({
          showActiveFilters: true
        });
      }} variant="text" scheme="light">
            <Typography variant="body3" color="link">
              + {t('filter.moreFiltersText', {
            count: numberOfHiddenFilters
          })}
            </Typography>
          </Button>
        </Tooltip>}

      <Grid>
        <Button scheme="light" variant={activeFilters.length > 0 ? 'text' : 'tonal'} onClick={() => {
        handleOnFilterClick({
          showActiveFilters: false
        });
      }} {...activeFilters.length === 0 && {
        leftIcon: <AdvancedFilterIcon />
      }}>
          {t('buttons.addFilters')}
        </Button>
      </Grid>
    </StyledAddFiltersGrid>;
};
export default AddFiltersGrid;
const StyledAddFiltersGrid = styled(Grid, {
  shouldForwardProp: prop => prop !== 'hasFilters'
})<{
  hasFilters: boolean;
}>(({
  theme,
  hasFilters
}) => ({
  ...(hasFilters && {
    padding: `${theme.spacing(0)} ${theme.spacing(0)} ${theme.spacing(0)} ${theme.spacing(1)}`,
    backgroundColor: theme.tokens.background.main.pressed,
    borderRadius: theme.borderRadius.large
  }),
  paddingLeft: hasFilters ? undefined : 0
}));
const StyledGradientGrid = styled(Grid)(({
  theme
}) => ({
  left: 'calc(100% - 32px)',
  pointerEvents: 'none',
  background: `linear-gradient(270deg, ${theme.tokens.background.main.pressed} 0%, transparent 100%)`
}));